import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useContext, useEffect, useState, useRef } from 'react';
import { ScreenContext } from 'context/ScreenContext';
import CustomMobileDateTimePicker from './CustomMobileDateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDispatch, useSelector } from 'react-redux';
import Util from 'utils/Util';
import { setPreOrderDateTimeDetails } from 'modules/basket/actions';
import { Dialog, DialogContent } from '@mui/material';
import constants from 'utils/constants';
import { 
  trackCateringOrderDatetimeCartAdjustmentChangeClicked,
  trackCateringOrderDatetimeCartAdjustmentDatetimeChanged,
  trackCateringOrderDatetimeCartAdjustmentChangeCompleted
} from 'middleware/helper';
import dayjs from 'dayjs';

const { DEFAULT_TIMEZONE } = constants;

export default function ResponsiveDateTimePickers({ 
  open = true, 
  onClose, 
  onDateTimeChange,
  initialValue,
  order,
  storeTimezone
}) {
  const [value, setValue] = useState(initialValue);
  const { mobile } = useContext(ScreenContext)
  const pickupStores = useSelector(state => state.location.getIn(['pickupStores']))
  const selectedStore = useSelector(state => state.location.getIn(['selectedStore']))
  const cateringStore = useSelector(state => state.catering?.store)
  const cateringSchedule = useSelector(state => state.catering?.schedule)
  const hasPlatterItems = useSelector(state => state.basket.get('hasPlatterItems'))
  const preOrderMinLeadTime = useSelector(state => state.configuration.get('min_lead_time'))
  const preOrderMaxLeadTime = useSelector(state => state.configuration.get('max_lead_time'))
  const dispatch = useDispatch()
  
  const previousDateTimeRef = useRef(null)
  const hasTrackedChangeClicked = useRef(false)
  
  // Check if this is a catering session
  const isCateringSession = hasPlatterItems || !!(cateringSchedule?.isValid)

  // Use catering store if available, otherwise fall back to selected store from location
  const activeStore = cateringStore || (selectedStore ? selectedStore.toJS() : null)
  const storeId = activeStore?.store_id || null
  // Get timezone from prop or store, fallback to default
  const timezone = storeTimezone || activeStore?.time_zone || DEFAULT_TIMEZONE
  const storeDetails = storeId ? pickupStores.filter(store => store.store_id === storeId) : []
  // If store details not found in pickupStores, use the catering store directly
  const resolvedStoreDetails = storeDetails.length > 0 ? storeDetails : (cateringStore ? [cateringStore] : [])
  let isValidWorkingDate = () => true;
  let isValidWorkingTime = () => false;

  // Sync value when initialValue changes
  useEffect(()=>{
    const currTime = value?.valueOf?.() ?? value
    const initTime = initialValue?.valueOf?.() ?? initialValue
    if (currTime === initTime) return;
    setValue(initialValue);
  },[initialValue])
  
  // Track when date/time picker is opened for catering
  useEffect(() => {
    if (open && isCateringSession && !hasTrackedChangeClicked.current) {
      const currentDate = cateringSchedule?.date || (initialValue ? dayjs(initialValue).format('YYYY-MM-DD') : null)
      const currentTime = cateringSchedule?.time || (initialValue ? dayjs(initialValue).format('HH:mm') : null)
      const pageContext = window.location.pathname
      
      if (currentDate && currentTime) {
        trackCateringOrderDatetimeCartAdjustmentChangeClicked(currentDate, currentTime, pageContext)
        previousDateTimeRef.current = { date: currentDate, time: currentTime }
        hasTrackedChangeClicked.current = true
      }
    }
    
    if (!open) {
      hasTrackedChangeClicked.current = false
    }
  }, [open, isCateringSession])

  const handlePickerChange = (newValue) => {
    setValue(newValue);
    
    // Track catering date/time change
    if (isCateringSession && newValue) {
      const newDate = dayjs(newValue).format('YYYY-MM-DD')
      const newTime = dayjs(newValue).format('HH:mm')
      const oldDate = previousDateTimeRef.current?.date || cateringSchedule?.date
      const oldTime = previousDateTimeRef.current?.time || cateringSchedule?.time
      
      if (oldDate && oldTime && (newDate !== oldDate || newTime !== oldTime)) {
        trackCateringOrderDatetimeCartAdjustmentDatetimeChanged(oldDate, oldTime, newDate, newTime)
        
        // Track change completed when picker closes
        if (onClose) {
          trackCateringOrderDatetimeCartAdjustmentChangeCompleted(newDate, newTime, 'pending')
        }
      }
      
      previousDateTimeRef.current = { date: newDate, time: newTime }
    }
    
    if (onDateTimeChange) {
      onDateTimeChange(newValue);
    }
    dispatch(setPreOrderDateTimeDetails(newValue));
  }

  const storeOp = resolvedStoreDetails?.[0]?.storeOperational || constants.fallBackStoreOperational;
  if (storeOp) {
    const validFns = Util.getValidDateandTime(storeOp,preOrderMinLeadTime,preOrderMaxLeadTime);
    isValidWorkingDate = validFns.isValidWorkingDate;
    isValidWorkingTime = validFns.isValidWorkingTime;
  }

  const { h: openH, m: openM } = Util.parseWorkingHour(storeOp?.workingHours?.from);
  const { h: closeH, m: closeM } = Util.parseWorkingHour(storeOp?.workingHours?.to);

  const clampToWindow = (djs) => {
    if (!storeOp) return djs;
    const h = djs.hour();
    const m = djs.minute();

    const beforeOpen = h < openH || (h === openH && m < openM);
    const afterClose = h > closeH || (h === closeH && m > closeM);

    if (beforeOpen) return djs.hour(openH).minute(openM).second(0).millisecond(0);
    if (afterClose) return djs.hour(closeH).minute(closeM).second(0).millisecond(0);
    return djs;
  }

  // Check if we're in modal mode (when onClose is provided)
  const isModalMode = typeof onClose === 'function';

  // If modal mode and not open, don't render anything
  if (isModalMode && !open) {
    return null;
  }

  // If modal mode, wrap in Dialog
  if (isModalMode) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }}
        PaperProps={{
          style: { 
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: '0',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogContent style={{ padding: 0 }}>
          <div className="custom-datetime-wrapper">
            <LocalizationProvider dateAdapter={mobile ? AdapterDayjs : AdapterDateFns}>
              <CustomMobileDateTimePicker
                value={value}
                onChange={handlePickerChange}
                isValidWorkingDate={isValidWorkingDate}
                openH={openH}
                openM={openM}
                closeH={closeH}
                closeM={closeM}
                clampToWindow={clampToWindow}
                preOrderMinLeadTime={preOrderMinLeadTime}
                preOrderMaxLeadTime={preOrderMaxLeadTime}
                mobile={mobile}
                direct={true}
                onClose={onClose}
                order={order}
                storeTimezone={timezone}
              />
            </LocalizationProvider>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Default mode for checkout page
  return (
    <div className="custom-datetime-wrapper">
      <LocalizationProvider dateAdapter={mobile ? AdapterDayjs : AdapterDateFns}>
        <CustomMobileDateTimePicker
          value={value}
          onChange={handlePickerChange}
          isValidWorkingDate={isValidWorkingDate}
          openH={openH}
          openM={openM}
          closeH={closeH}
          closeM={closeM}
          clampToWindow={clampToWindow}
          preOrderMinLeadTime={preOrderMinLeadTime}
          preOrderMaxLeadTime={preOrderMaxLeadTime}
          mobile={mobile}
          storeTimezone={timezone}
        />
      </LocalizationProvider>
    </div>
  );

}